<template>
  <NotFoundPage v-if="isNotFound" />
  <RedocWrapper
    v-else
    :specOrSpecUrl="spec"
    @changeIsRedocLoaded="onChangeIsRedocLoaded"
  />
</template>

<script>
/* eslint-disable no-param-reassign */
import {
  ref,
  reactive,
  inject,
  onBeforeMount,
} from 'vue'

import { useRoute } from 'vue-router'

import {
  getDoc,
  getSectionsByDocId,
  getManagementByEntityType,
  getManagementByEntityTypeAndId,
} from '@/api/apiDocV2'

import NotFoundPage from '@/views/NotFound'
import RedocWrapper from '@/components/RedocWrapper'

import {
  transformToNested,
  renderTableDataHtml,
  renderTableDataByPlatformHtml,
  renderStringifiedJsonTableRowArray,
  processContentLink,
  processContentBrand,
  shouldDisplay,
} from '@/components/ApiDoc/libs'

export default {
  name: 'ApiDocV2',
  components: {
    RedocWrapper,
    NotFoundPage,
  },
  setup() {
    const nodeEnv = inject('nodeEnv')
    const isDev = inject('isDev')

    const route = useRoute()
    const { wallet: walletName, edition } = route.params

    const isNotFound = ref(false)
    const spec = reactive({})
    const docData = ref({})
    const templateData = ref({})
    const sectionIdMap = ref({})
    const sectionTreeData = ref([])
    const apiMap = ref({})
    const platformsMap = ref({})

    const findLastIndexBySectionType = (treeData) => {
      const result = treeData.reduce((acc, section, index) => {
        const { sectionType } = section
        if (acc[sectionType] == null) acc[sectionType] = 0
        if (index > acc[sectionType]) acc[sectionType] = index
        return acc
      }, {})

      if (!result.appendices) result.appendices = treeData.length - 1

      return result
    }

    const processContent = content => processContentLink(processContentBrand(content, templateData.value.brandText), sectionIdMap.value)

    const processRestrictedContent = (restricted) => {
      if (!restricted) return ''
      const { content } = restricted

      if (!content) return ''
      if (!shouldDisplay(restricted, docData.value)) return ''
      return content
    }

    const simpleHtmlMinify = html => html
      .replace(/\s+/g, ' ') // 將多個空白替換為單個空格
      // .replace(/>\s+</g, '><') // 移除標籤之間的空白
      .replace(/\s+>/g, '>') // 移除結束標籤前的空白
      .replace(/<\s+/g, '<') // 移除開始標籤後的空白
      .replace(/\s+"/g, '"') // 移除屬性前的空白
      .replace(/"\s+/g, '"') // 移除屬性後的空白
      .trim() // 移除首尾空白

    const getStringifiedJsonTableHtml = (field) => {
      const trHtml = renderStringifiedJsonTableRowArray(field, docData.value).map(childField => `<tr>
          <td>${childField.fieldName}</td>
          <td>
            ${childField.fieldType}
            ${childField.fieldType === 'number' && childField.numberType ? `<${childField.numberType}>` : ''}
          </td>
          <td>
            ${childField.maxLength ? `${childField.maxLength}` : ''}
            ${!childField.maxLength && childField.decimalPlace == null ? '-' : ''}
            ${childField.numberType === 'float' && childField.decimalPlace != null ? `${childField.decimalPlace} decimal places<br>支援到小数第 ${childField.decimalPlace} 位` : ''}
          </td>
          <td>${processContent(childField.content)}</td>
        </tr>`).join('\n')

      return simpleHtmlMinify(`<table>
        <thead>
          <tr>
            <td>Field 栏位</td>
            <td>Type 类型</td>
            <td>Length Limit 长度限制</td>
            <td>Description 描述</td>
          </tr>
        </thead>
        <tbody>${trHtml}</tbody>
      </table>`)
    }

    const recurseFieldsToSchema = (currentFieldData, level = 0) => {
      const requiredFields = []

      const properties = (currentFieldData.fieldsOrder || []).reduce((acc, fieldId) => {
        const currentField = currentFieldData.fields[fieldId]
        if (!shouldDisplay(currentField, docData.value)) return acc

        const {
          fieldName,
          fieldType,
          content,
          maxLength,
          numberType,
          decimalPlace,
          required,
        } = currentField || {}

        if (required) requiredFields.push(fieldName)

        const property = {
          type: fieldType,
          description: processContent(content),
        }

        switch (fieldType) {
          case 'string':
            if (maxLength) property.maxLength = maxLength
            break

          case 'number':
            if (numberType) property.format = numberType
            if (numberType === 'float' && decimalPlace) {
              const precision = 1000000 // 處理浮點數精度問題 (0.1 ** 4 = 0.00010000000000000002)
              property.multipleOf = Math.round(((0.1 ** decimalPlace) * precision)) / precision
            }
            break

          case 'array of strings':
            property.type = 'array'
            property.items = {
              type: 'string',
            }
            break

          case 'array of numbers':
            property.type = 'array'
            property.items = {
              type: 'number',
            }
            break

          case 'array of objects':
            property.type = 'array'
            property.items = {
              type: 'object',
              ...recurseFieldsToSchema(currentField, level + 1),
            }
            break

          case 'object':
            property.type = 'object'

            const { // eslint-disable-line no-case-declarations
              properties: childProperties,
              required: childRequired,
            } = recurseFieldsToSchema(currentField, level + 1)

            property.properties = childProperties
            property.required = childRequired
            break

          case 'stringified json':
            property.type = 'string'
            property.description += `\n\n${getStringifiedJsonTableHtml(currentField)}`
            break

          case 'boolean':
          default:
        }

        acc[fieldName] = property
        return acc
      }, {})

      return { properties, required: requiredFields }
    }

    const recurseFieldsToExamples = (currentFieldData, sectionExamples = {}) => {
      const result = {}

      currentFieldData.fieldsOrder.forEach((fieldId) => {
        const currentField = currentFieldData.fields[fieldId]

        const {
          fieldName,
          fieldType,
          examples,
        } = currentField

        if (!shouldDisplay(currentField, docData.value)) return

        if (['stringified json', 'array of objects', 'object'].includes(fieldType) && !examples) { // 這三種沒有 examples，子欄位才有
          const exampleValue = recurseFieldsToExamples(currentField, sectionExamples)
          if (!exampleValue) return

          Object.entries(exampleValue).forEach(([exampleName, { value }]) => {
            if (!Object.keys(value).length) return

            if (!result[exampleName]) result[exampleName] = {}
            if (!result[exampleName].value) result[exampleName].value = {}

            if (fieldType === 'stringified json') {
              result[exampleName].value[fieldName] = JSON.stringify(value)
            } else if (fieldType === 'array of objects') {
              result[exampleName].value[fieldName] = []
              result[exampleName].value[fieldName][0] = value
            } else {
              result[exampleName].value[fieldName] = value
            }
          })
        } else if (examples) {
          Object.entries(examples).forEach(([exampleId, exampleValue]) => {
            if (!exampleValue) return
            const { exampleName } = sectionExamples[exampleId]

            const formatExampleValue = (val) => {
              let resultVal = val
              switch (fieldType) {
                default:
                case 'string':
                  if (val === '\'\'' || val === '""') {
                    resultVal = ''
                  } else if (val === 'null') {
                    resultVal = null
                  } else {
                    resultVal = val
                  }
                  break

                case 'json':
                  try {
                    resultVal = JSON.parse(val)
                  } catch (error) {
                    resultVal = val
                  }
                  break

                case 'number':
                  resultVal = Number(val)
                  break

                case 'boolean':
                  resultVal = val === 'true'
                  break

                case 'array of strings': {
                  if (/^\[\s*\]$/.test(val)) {
                    resultVal = []
                  } else {
                    const normalizeString = str => str
                    // eslint-disable-next-line no-useless-escape
                      .replace(/[\[\]'"]/g, '') // 移除方括號和引號
                      .replace(/\s*,\s*/g, ',') // 移除逗號前後的空格
                      .trim()

                    resultVal = normalizeString(val).split(',')
                  }
                  break
                }

                case 'array of numbers': {
                  if (/^\[\s*\]$/.test(val)) {
                    resultVal = []
                  } else {
                    resultVal = JSON.parse(val)
                  }
                }
              }

              return resultVal
            }

            if (exampleValue.arrayLength) {
              // console.log(fieldName, exampleValue)
              if (!result[exampleName]) result[exampleName] = {}
              if (!result[exampleName].value) result[exampleName].value = {}
              if (!result[exampleName].value[fieldName]) result[exampleName].value[fieldName] = []

              const childExampleValue = recurseFieldsToExamples(currentField, sectionExamples)
              // console.log('*****childExampleValue', childExampleValue)

              Object.values(childExampleValue).forEach(({ value }) => {
                // console.log('*****value', value)
                Array.from({ length: exampleValue.arrayLength }).forEach((_, idx) => {
                  Object.entries(value).forEach(([key, val]) => {
                    // console.log('*****key', key, '*****val', val)
                    if (!result[exampleName].value[fieldName][idx]) result[exampleName].value[fieldName][idx] = {}
                    result[exampleName].value[fieldName][idx][key] = Array.isArray(val) ? val[idx] : val
                  })
                })
              })
              return
            }

            if (!result[exampleName]) result[exampleName] = {}
            if (!result[exampleName].value) result[exampleName].value = {}

            if (Array.isArray(exampleValue)) {
              exampleValue.forEach((item, idx) => {
                if (!result[exampleName].value[fieldName]) result[exampleName].value[fieldName] = []
                result[exampleName].value[fieldName][idx] = formatExampleValue(item)
              })
              return
            }

            result[exampleName].value[fieldName] = formatExampleValue(exampleValue)
          })
        }
      })

      if (!Object.keys(result).length) return null

      const sortedResult = Object.fromEntries(
        Object.entries(result)
          .sort(([a], [b]) => {
            const aHasSuccess = a.toLowerCase().includes('success')
            const bHasSuccess = b.toLowerCase().includes('success')
            const aHasSexybcrt = a.toLowerCase().includes('sexybcrt')
            const bHasSexybcrt = b.toLowerCase().includes('sexybcrt')

            // 如果 a 有 success 但 b 沒有，a 排在前面
            if (aHasSuccess && !bHasSuccess) return -1
            // 如果 b 有 success 但 a 沒有，b 排在前面
            if (!aHasSuccess && bHasSuccess) return 1
            // 如果都有 success，比較 sexybcrt
            if (aHasSuccess && bHasSuccess) {
              if (aHasSexybcrt && !bHasSexybcrt) return 1
              if (!aHasSexybcrt && bHasSexybcrt) return -1
            }
            // 如果都沒有 success，比較 sexybcrt
            if (!aHasSuccess && !bHasSuccess) {
              if (aHasSexybcrt && !bHasSexybcrt) return -1
              if (!aHasSexybcrt && bHasSexybcrt) return 1
            }
            return 0
          })
          .map(([name, { value }]) => [name, { value }]),
      )

      return sortedResult
    }

    const getAppendixDescription = (appendixTreeData) => {
      const result = appendixTreeData.treeChildren?.reduce((acc, childSection) => {
        if (!shouldDisplay(childSection, docData.value)) return acc
        const {
          treeTitle,
          content,
          restricted,
          tableData,
        } = childSection

        acc += `<h2>${treeTitle}</h2>\n\n`
        acc += `${processContent(content)}\n\n${processRestrictedContent(restricted)}\n\n`

        if (tableData) {
          acc += `${renderTableDataByPlatformHtml({
            tableData,
            platformsMap: tableData.columns?.api ? apiMap.value : platformsMap.value,
            brandText: templateData.value.brandText,
            sectionIdMap: sectionIdMap.value,
          })}\n\n`
        }

        return acc
      }, '')

      return result
    }

    const getApiDoc = async () => {
      const wallet = walletName.match(/^(\w+)-wallet$/)?.[1] || ''
      if (!wallet || !['single', 'multi'].includes(wallet)) {
        isNotFound.value = true
        return
      }

      const isStage = nodeEnv.value === 'stage'
      console.log('nodeEnv', nodeEnv.value, 'isDev', isDev.value, 'isStage', isStage)
      const docId = `${nodeEnv.value === 'development' || isStage ? 'allwecan.info' : window.location.host}#${wallet}#${edition}`
      // const docId = `${isDev || isStage ? 'sanatechinfo.com' : window.location.host}#${wallet}#${edition}`
      // const docId = `${isDev || isStage ? 'in2info.cc' : window.location.host}#${wallet}#${edition}`

      const { data: doc } = await getDoc(docId)
      if (!doc) {
        isNotFound.value = true
        return
      }

      document.title = doc.docTitle

      docData.value = doc
      console.log('getDoc', docData.value)

      const { data: template } = await getManagementByEntityTypeAndId('template', doc.templateId)
      templateData.value = template
      console.log('template', template)
      let favicon = document.querySelector('link[rel="icon"]')
      if (!template.faviconUrl) {
        if (favicon) {
          favicon.remove()
        }
        // 強制清除 favicon 快取 - 使用空白圖示覆蓋
        const clearFavicon = document.createElement('link')
        clearFavicon.rel = 'icon'
        clearFavicon.href = 'data:image/x-icon;base64,'
        document.head.appendChild(clearFavicon)

        setTimeout(() => clearFavicon.remove(), 50) // 短暫延遲後移除，確保瀏覽器處理了空白圖示
      } else if (!favicon) {
        favicon = document.createElement('link')
        favicon.rel = 'icon'
        favicon.href = template.faviconUrl
        document.head.appendChild(favicon)
      } else {
        favicon.href = template.faviconUrl
      }

      const { data: sections } = await getSectionsByDocId(docId)
      const treeData = transformToNested({ data: sections })
      sectionTreeData.value = treeData
      console.log('treeData', treeData)

      treeData.forEach((section) => {
        sectionIdMap.value[section.sectionId] = section
        if (section.treeChildren) {
          section.treeChildren.forEach((child) => {
            sectionIdMap.value[child.sectionId] = child
          })
        }
      })

      const sectionTypeIndexes = findLastIndexBySectionType(treeData)
      console.log('sectionTypeIndexes', sectionTypeIndexes)

      apiMap.value = treeData.slice(sectionTypeIndexes.info + 1, sectionTypeIndexes.apis + 1).reduce((acc, { treeChildren = [] }) => {
        if (treeChildren.length) {
          treeChildren.forEach(({ operationId, ...child }) => {
            if (operationId && shouldDisplay(child, docData.value)) acc[operationId] = true
          })
        }
        return acc
      }, {})
      console.log('apiMap', apiMap.value)

      const { data: platforms } = await getManagementByEntityType('platform')
      platformsMap.value = platforms.reduce((acc, { id, platformName }) => {
        const docPlatforms = docData.value.platforms || []
        const docExcludedPlatforms = docData.value.excludedPlatforms || []
        if ((!docPlatforms?.length && !docExcludedPlatforms.includes(id)) || (docPlatforms.length && docPlatforms.includes(id))) acc[id] = platformName
        return acc
      }, {})

      platformsMap.value['{{brand}}'] = template.brandText
      console.log('platformsMap', platformsMap.value)

      Object.assign(spec, {
        openapi: '3.0.0',
        servers: [{ url: 'https://{host}' }],
        info: {
          version: null,
          title: doc.docTitle,
          'x-logo': template.logoUrl && {
            url: template.logoUrl,
            altText: `${template.brandText} logo`,
            href: window.location.pathname,
          },
        },
        components: {
          parameters: {
            'Content-Type': {
              name: 'Content-Type',
              in: 'header',
              required: true,
              style: 'simple',
              schema: {
                type: 'string',
                example: 'application/x-www-form-urlencoded',
              },
            },
          },
        },
      })

      spec.info.description = treeData.slice(0, sectionTypeIndexes.info + 1).reduce((acc, section) => {
        if (!shouldDisplay(section, docData.value)) return acc

        const {
          treeTitle,
          treeChildren,
          content,
          tableData,
        } = section

        acc += `# ${treeTitle}\n\n`
        if (content) acc += `${processContent(content)}\n\n`

        if (tableData) {
          acc += `${renderTableDataHtml({
            tableData,
            platformsMap: platformsMap.value,
            brandText: template.brandText,
            sectionIdMap: sectionIdMap.value,
          })}\n\n`
        }

        if (treeChildren) {
          treeChildren.forEach((child) => {
            if (shouldDisplay(child, docData.value)) acc += `## ${child.treeTitle}\n\n${processContent(child.content)}\n\n`
          })
        }
        return acc
      }, '')

      spec.tags = treeData.slice(sectionTypeIndexes.info + 1, sectionTypeIndexes.appendices + 1).map(({ treeTitle, tag }) => ({
        name: tag,
        'x-displayName': treeTitle,
        ...(tag === 'appendix' && { description: getAppendixDescription(treeData[sectionTypeIndexes.appendices]) }),
      }))

      spec.paths = treeData.slice(sectionTypeIndexes.info + 1, sectionTypeIndexes.apis + 1).reduce((acc, section) => {
        const { tag, treeChildren } = section

        if (treeChildren) {
          treeChildren.forEach((child) => {
            const {
              path,
              treeTitle,
              content,
              operationId,
              request,
              response,
              examples,
            } = child

            if (!shouldDisplay(child, docData.value)) return

            const requestSchema = recurseFieldsToSchema(request)
            const responseSchema = recurseFieldsToSchema(response)

            const requestExamples = recurseFieldsToExamples(request, examples)
            const responseExamples = recurseFieldsToExamples(response, examples)

            acc[path] = {
              post: {
                summary: treeTitle,
                description: processContent(content),
                operationId,
                parameters: [{ $ref: '#/components/parameters/Content-Type' }],
                tags: [tag],
                requestBody: {
                  // description: 'Request Parameters',
                  content: {
                    'application/x-www-form-urlencoded': {
                      schema: requestSchema,
                      ...(requestExamples && { examples: requestExamples }),
                    },
                  },
                  required: true,
                },
                responses: {
                  200: {
                    // description: '',
                    content: {
                      'application/json;charset=UTF-8': {
                        schema: responseSchema,
                        ...(responseExamples && { examples: responseExamples }),
                      },
                    },
                  },
                },
              },
            }
          })
        }

        return acc
      }, {})

      console.log('spec', spec)
    }

    const onChangeIsRedocLoaded = (value) => { if (value && window.opener) window.opener.postMessage({ isRedocLoaded: true }, '*') }

    onBeforeMount(getApiDoc)

    return {
      spec,
      onChangeIsRedocLoaded,
      isNotFound,
    }
  },
}
</script>

<style>
@media print{
  @page {
    size: A4 landscape;
  }
  .platform-table {
    display: table!important;
    table-layout: fixed;
    font-size: 12px;
    overflow: hidden;
  }
  .platform-table th {
    height: 120px;
  }
  .platform-table tr th:first-child {
    width: 90px;
  }
  .vertical {
    width: 45px;
    transform: rotate(90deg) translateX(45px);
    transform-origin: top right;
  }
}
</style>
